import type { DateTime } from 'luxon';
import { SAMPLE_LOCK_SCREEN_TIME } from '@/components/campaign-composer/korza-studio';
import type { StudioAnchor } from '@/components/korza-studio/studio-framing';
import type { WelcomeDelay } from '@/lib/app-editor/welcome-notification-form';

// Welcome notification preview in the Korza Studio look: the app is
// installed at the Studio's sample time (9:41) and the push arrives
// `delayMinutes` later, so the lock screen clock (and date) roll to then.

/** When the welcome push reaches the phone of someone installing at 9:41. */
export function welcomeArrival(
  delayMinutes: WelcomeDelay,
  installedAt: DateTime = SAMPLE_LOCK_SCREEN_TIME
): DateTime {
  return installedAt.plus({ minutes: delayMinutes });
}

/** The "Chega" end of the timeline pill. */
export type ArrivalLabel =
  | { kind: 'now' }
  | { kind: 'sameDay' }
  | { kind: 'days'; days: number };

export function arrivalLabel(delayMinutes: WelcomeDelay): ArrivalLabel {
  if (delayMinutes === 0) return { kind: 'now' };
  const days = Math.floor(delayMinutes / 1440);
  return days === 0 ? { kind: 'sameDay' } : { kind: 'days', days };
}

const ARROW_MIN_PX = 20;
const ARROW_MAX_PX = 72;
const LONGEST_DELAY_MINUTES = 10080;

/** The pill's arrow grows with the wait (log scale, so minutes still show). */
export function timelineArrowWidth(delayMinutes: WelcomeDelay): number {
  const t = Math.log1p(delayMinutes) / Math.log1p(LONGEST_DELAY_MINUTES);
  return Math.round(ARROW_MIN_PX + (ARROW_MAX_PX - ARROW_MIN_PX) * t);
}

/** What the phone is framed on: the push (message, link) or the clock (when). */
export type StudioFocus = 'notification' | 'clock';

/** The clock is at the top of the lock screen; the iOS 26 push sits low. */
export function welcomeStudioAnchor(focus: StudioFocus): StudioAnchor {
  return focus === 'clock' ? 'top' : 'bottom';
}
